import React, { useEffect, useRef, useState } from 'react';
import { CalendarClock, ChevronDown, ChevronLeft, ChevronRight, Link2 } from 'lucide-react';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import { useToast } from '../../../../../components/ui/toast/ToastContext';

const formatCurrency = (value) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) return '—';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(parsedValue);
};

const productTypeLabel = (productType) => {
  if (!productType) return '—';

  return productType;
};

const operationColors = (operationType) => {
  const normalized = (operationType || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  if (normalized.includes('resgate') || normalized.includes('retirada')) {
    return { dot: 'bg-redHighlight', text: 'text-redDeep' };
  }

  if (normalized.includes('aplicacao') || normalized.includes('aporte')) {
    return { dot: 'bg-blueHighlight', text: 'text-blueMedium' };
  }

  return { dot: 'bg-tealMedium', text: 'text-tealDark' };
};

const HistorySelect = ({ label, options, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const closeDropdown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeDropdown);
    return () => document.removeEventListener('mousedown', closeDropdown);
  }, []);

  return (
    <div ref={containerRef} className="relative w-36 flex-shrink-0">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border border-bluePale bg-white text-sm text-blueDarker hover:border-tealMedium focus:outline-none focus:border-tealMedium transition-colors"
      >
        <span className={`truncate ${value ? 'text-blueDarker' : 'text-blueSteel'}`}>{value || label}</span>
        <ChevronDown
          size={17}
          strokeWidth={1.5}
          className={`text-blueSteel flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute top-full left-0 z-50 mt-1 min-w-full max-h-64 overflow-y-auto rounded-xl border border-bluePale bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="option"
            aria-selected={!value}
            onClick={() => {
              onChange('');
              setIsOpen(false);
            }}
            className={`block w-full whitespace-nowrap px-4 py-2.5 text-left text-sm text-blueDarker hover:bg-blueLight ${
              !value ? 'bg-blueLight' : ''
            }`}
          >
            Todos
          </button>

          {options.map((option) => (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={value === option}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={`block w-full whitespace-nowrap px-4 py-2.5 text-left text-sm text-blueDarker hover:bg-blueLight ${
                value === option ? 'bg-blueLight' : ''
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DateField = ({ label, value, onChange }) => (
  <input
    type="date"
    aria-label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className="w-36 px-4 py-2.5 rounded-xl border border-bluePale bg-white text-sm text-blueDarker focus:outline-none focus:border-tealMedium transition-colors flex-shrink-0"
  />
);

const OperationBadge = ({ operationType }) => {
  const colors = operationColors(operationType);

  return (
    <span
      className={`inline-flex max-w-full items-center gap-2 rounded-full bg-blueWhisper px-3 py-1 text-xs font-semibold tracking-wide ${colors.text}`}
      title={operationType}
    >
      <span className={`block h-2 w-2 flex-shrink-0 rounded-full ${colors.dot}`} />
      <span className="truncate">{(operationType || 'Não informado').toUpperCase()}</span>
    </span>
  );
};

const HistoryRow = ({ operation, highlighted }) => (
  <div
    className={`grid items-center gap-4 rounded-xl px-4 py-4 text-base text-blueDarker ${
      highlighted ? 'bg-blueLight' : 'bg-white'
    }`}
    style={{
      gridTemplateColumns: '130px 170px minmax(220px, 1fr) 135px 105px 125px',
    }}
  >
    <div className="min-w-0">
      <OperationBadge operationType={operation.operationType.split(' ')[0]} />
    </div>
    <span className="truncate">{productTypeLabel(operation.productType)}</span>
    <span className="truncate font-semibold" title={operation.productName}>
      {operation.productName || '—'}
    </span>
    <span className="whitespace-nowrap">{operation.requestedAt || '—'}</span>
    <span className="whitespace-nowrap">{operation.settledAt || '—'}</span>
    <span className="whitespace-nowrap">{formatCurrency(operation.value)}</span>
  </div>
);

const History = ({ apiClientId }) => {
  const { addToast } = useToast();
  const [operationType, setOperationType] = useState('');
  const [productType, setProductType] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [operations, setOperations] = useState([]);
  const [filters, setFilters] = useState({
    operationTypes: [],
    productTypes: [],
    statuses: [],
  });
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildParams = (page) => {
    const params = new URLSearchParams();

    if (page) params.set('page', page);
    params.set('per_page', '10');
    if (operationType) params.set('operation_type', operationType);
    if (productType) params.set('product_type', productType);
    if (status) params.set('status', status);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);

    return params;
  };

  const validPeriod = () => {
    if (!dateFrom || !dateTo || dateFrom <= dateTo) return true;

    addToast({
      type: 'error',
      message: 'A data inicial não pode ser posterior à data final.',
    });
    return false;
  };

  const fetchOperations = async (page = 1) => {
    if (!validPeriod()) return;

    setLoading(true);

    try {
      const response = await fetch(`/app/analyst/client/${apiClientId}/dashboard/history?${buildParams(page)}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erro ao carregar o histórico');
      }

      const data = camelizeKeys(await response.json());
      setOperations(data.operations || []);
      setFilters(data.filters || { operationTypes: [], productTypes: [], statuses: [] });
      setPagination(data.pagination || null);
    } catch (error) {
      addToast({ type: 'error', message: error.message });
      setOperations([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOperations();
  }, [apiClientId]);

  const handlePageChange = (page) => {
    if (!pagination || page < 1 || page > pagination.totalPages) return;
    fetchOperations(page);
  };

  const handleExport = () => {
    if (!validPeriod()) return;

    const params = buildParams();
    params.delete('per_page');
    window.open(`/app/analyst/client/${apiClientId}/dashboard/history/export?${params}`, '_blank');
  };

  console.log('History render', {
    operationType,
    productType,
    status,
    dateFrom,
    dateTo,
    operations,
    filters,
    pagination,
  });

  return (
    <div className="bg-white rounded-custom20 p-4 flex flex-col gap-6" style={{ minHeight: '600px' }}>
      <div
        className="bg-blueLight flex flex-row flex-wrap items-center gap-4 px-4 py-3 justify-between"
        style={{ borderRadius: '12px' }}
      >
        <div className="flex items-center gap-3 flex-shrink-0 mr-2">
          <Link2 size={20} strokeWidth={1.7} className="text-tealBase" />
          <h2 className="text-blueDarker text-xl font-semibold whitespace-nowrap">Histórico</h2>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <HistorySelect
            label="Operação"
            options={filters.operationTypes || []}
            value={operationType}
            onChange={setOperationType}
          />
          <HistorySelect
            label="Produto"
            options={filters.productTypes || []}
            value={productType}
            onChange={setProductType}
          />
          <HistorySelect label="Situação" options={filters.statuses || []} value={status} onChange={setStatus} />

          <div className="h-8 w-px bg-bluePale flex-shrink-0" />

          <DateField label="De" value={dateFrom} onChange={setDateFrom} />
          <DateField label="Até" value={dateTo} onChange={setDateTo} />

          <button
            type="button"
            onClick={() => fetchOperations(1)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-tealMedium hover:opacity-90 transition-opacity text-white text-sm font-bold border-r-2 border-b-2 border-tealBase flex-shrink-0 whitespace-nowrap"
          >
            <CalendarClock size={16} />
            FILTRAR PERÍODO
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: '850px' }}>
          <div
            className="grid items-center gap-4 px-4 py-3 text-base font-semibold text-blueDarker"
            style={{
              gridTemplateColumns: '130px 170px minmax(220px, 1fr) 135px 105px 125px',
            }}
          >
            <span>Operação</span>
            <span>Tipo</span>
            <span>Produto</span>
            <span>Solicitação</span>
            <span>Liquidação</span>
            <span>Valor</span>
          </div>

          <div className="flex flex-col gap-2">
            {loading ? (
              <div className="py-12 text-center text-sm text-blueDarker/50">Carregando histórico...</div>
            ) : operations.length === 0 ? (
              <div className="py-12 text-center text-sm text-blueDarker/50">Nenhuma operação encontrada.</div>
            ) : (
              operations.map((operation, index) => (
                <HistoryRow key={operation.id} operation={operation} highlighted={index % 2 === 0} />
              ))
            )}
          </div>
        </div>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={pagination.currentPage <= 1}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            className="flex items-center gap-1 rounded-lg border border-bluePale px-3 py-1.5 text-sm text-blueDarker transition-colors hover:bg-blueLight disabled:opacity-40"
          >
            <ChevronLeft size={14} />
            Anterior
          </button>
          <span className="text-sm text-blueDarker/70">
            Página {pagination.currentPage} de {pagination.totalPages}
          </span>
          <button
            type="button"
            disabled={pagination.currentPage >= pagination.totalPages}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            className="flex items-center gap-1 rounded-lg border border-bluePale px-3 py-1.5 text-sm text-blueDarker transition-colors hover:bg-blueLight disabled:opacity-40"
          >
            Próximo
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      <div className="mt-auto flex justify-end pt-4">
        <button
          type="button"
          onClick={handleExport}
          className="rounded-xl border-b-2 border-r-2 border-tealBase bg-tealMedium px-6 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90"
        >
          EXPORTAR HISTÓRICO
        </button>
      </div>
    </div>
  );
};

export default History;
